import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Sparkles, Loader2, Star } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const PACKAGES = [
  {
    id: 'single',
    name: 'Single Analysis',
    description: 'Perfect for first-time users',
    credits: 1,
    price: 6.99,
    perAnalysis: 6.99,
    savings: 0,
    badge: 'Try It'
  },
  {
    id: 'light',
    name: 'Light Package',
    description: 'Great for small families',
    credits: 3,
    price: 14.99,
    perAnalysis: 5.00,
    savings: 17,
    badge: 'Best for Parents'
  },
  {
    id: 'standard',
    name: 'Standard Package',
    description: 'Best value for regular practice',
    credits: 10,
    price: 38.99,
    perAnalysis: 3.90,
    savings: 44,
    badge: 'Most Popular',
    featured: true
  },
  {
    id: 'advanced',
    name: 'Advanced Package',
    description: 'For serious learners',
    credits: 25,
    price: 78.99,
    perAnalysis: 3.16,
    savings: 55,
    badge: 'Great Value'
  },
  {
    id: 'teacher',
    name: 'Teacher Package',
    description: 'Perfect for educators',
    credits: 100,
    price: 198.99,
    perAnalysis: 1.99,
    savings: 72,
    badge: 'For Classrooms'
  },
  {
    id: 'school',
    name: 'School Package',
    description: 'For schools and institutions',
    credits: 300,
    price: 498.99,
    perAnalysis: 1.66,
    savings: 76,
    badge: 'Best Value'
  }
];

export default function Pricing() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [loadingId, setLoadingId] = useState(null);
  const [error, setError] = useState(null);

  const handlePurchase = async (packageId) => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: '/pricing' } });
      return;
    }

    setLoadingId(packageId);
    setError(null);

    try {
      const response = await api.post('/payments/create-checkout', { packageId });
      window.location.href = response.data.url;
    } catch (err) {
      setError('Failed to start checkout. Please try again.');
      setLoadingId(null);
    }
  };

  return (
    <div className="py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-bastion-black/10 text-bastion-black px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Sparkles className="w-4 h-4" />
            Pay Per Use
          </div>
          <h1 className="section-title">Simple, Transparent Pricing</h1>
          <p className="section-subtitle">
            Choose the perfect package for your handwriting improvement journey
          </p>
        </div>

        {error && (
          <div className="max-w-md mx-auto mb-8 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-center">
            {error}
          </div>
        )}

        {/* Pricing Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {PACKAGES.map((pkg) => (
            <div
              key={pkg.id}
              className={`card relative ${
                pkg.featured
                  ? 'border-2 border-bastion-black shadow-lg'
                  : ''
              }`}
            >
              {/* Badge */}
              {pkg.badge && (
                <span
                  className={`absolute -top-3 right-4 px-3 py-1 rounded-full text-xs font-semibold ${
                    pkg.featured
                      ? 'bg-bastion-black text-white'
                      : 'bg-bastion-gray-100 text-bastion-gray-700'
                  }`}
                >
                  {pkg.badge}
                </span>
              )}

              {/* Icon */}
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                pkg.featured ? 'bg-bastion-black' : 'bg-bastion-gray-100'
              }`}>
                <Sparkles className={`w-6 h-6 ${
                  pkg.featured ? 'text-white' : 'text-bastion-black'
                }`} />
              </div>

              {/* Package Info */}
              <h3 className="text-xl font-semibold text-bastion-black mb-1">
                {pkg.name}
              </h3>
              <p className="text-bastion-gray-700 text-sm mb-4">
                {pkg.description}
              </p>

              {/* Price */}
              <div className="mb-4">
                <span className="text-4xl font-bold text-bastion-black">
                  ${pkg.price.toFixed(2)}
                </span>
                <p className="text-sm text-bastion-gray-700 mt-1">
                  {pkg.credits} analysis{pkg.credits > 1 ? 'es' : ''} included
                </p>
                {pkg.savings > 0 && (
                  <span className="inline-block mt-2 px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded">
                    Save {pkg.savings}%
                  </span>
                )}
              </div>

              {/* Features */}
              <ul className="space-y-2 mb-6">
                <li className="flex items-center gap-2 text-sm text-bastion-gray-800">
                  <Check className="w-4 h-4 text-green-500" />
                  ${pkg.perAnalysis.toFixed(2)}/analysis
                </li>
                <li className="flex items-center gap-2 text-sm text-bastion-gray-800">
                  <Check className="w-4 h-4 text-green-500" />
                  Complete AI analysis
                </li>
                <li className="flex items-center gap-2 text-sm text-bastion-gray-800">
                  <Check className="w-4 h-4 text-green-500" />
                  Instant suggestions
                </li>
                {pkg.credits >= 3 && (
                  <li className="flex items-center gap-2 text-sm text-bastion-gray-800">
                    <Check className="w-4 h-4 text-green-500" />
                    Track progress over time
                  </li>
                )}
                {pkg.credits >= 10 && (
                  <li className="flex items-center gap-2 text-sm text-bastion-gray-800">
                    <Check className="w-4 h-4 text-green-500" />
                    Long-term tracking
                  </li>
                )}
              </ul>

              {/* CTA */}
              <button
                onClick={() => handlePurchase(pkg.id)}
                disabled={loadingId === pkg.id}
                className={`w-full py-3 rounded-lg font-semibold transition-colors ${
                  pkg.featured
                    ? 'bg-bastion-black hover:bg-bastion-black-dark text-white'
                    : 'bg-white hover:bg-bastion-gray-50 text-bastion-black border border-bastion-gray-300'
                }`}
              >
                {loadingId === pkg.id ? (
                  <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                ) : pkg.credits === 1 ? (
                  'Start with 1 Analysis'
                ) : (
                  `Get ${pkg.credits} Analyses`
                )}
              </button>
            </div>
          ))}
        </div>

        {/* FAQ or Trust Badges */}
        <div className="mt-16 text-center">
          <div className="flex flex-wrap justify-center gap-8">
            <div className="flex items-center gap-2 text-bastion-gray-800">
              <Check className="w-5 h-5 text-green-500" />
              Secure payment with Stripe
            </div>
            <div className="flex items-center gap-2 text-bastion-gray-800">
              <Check className="w-5 h-5 text-green-500" />
              Credits never expire
            </div>
            <div className="flex items-center gap-2 text-bastion-gray-800">
              <Check className="w-5 h-5 text-green-500" />
              Free worksheets included
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
